/// <reference path="../../typings/browser.d.ts" />
/// <reference path="../../customtypings/selectize/selectize.d.ts" />
/// <reference path="../../customtypings/momentjs/index.d.ts" />

namespace Application.Controllers {
    'use strict';

    import IPaginatedList = Models.IPaginatedList;
    import SIMDataUsages = Models.SIMDataUsages;

    interface IPathParameters { 
        selectedSim: string;
    } 

    interface ISelectOption {
        value: string;
        text: string;
    }

    /**
     * NetworkNodeController class
     */
    export class SimUsageController {
        public static $name: string = "simUsageController";
        public static $inject: string[] = ['simService',
                                           'simPlanService',
                                           'ModalService',
                                           '$timeout',
                                           '$scope',
                                           '$routeParams',
                                           'timezoneService',
                                           'messageTranslationsService',
                                           ];

        private static USAGE_CHART_ID: string = '#usage-data-chart';
        private static REMAINING_CHART_ID: string = '#remaining-data-chart';
        private static USAGE_CHART_TOOLTIP_ID: string = SimUsageController.USAGE_CHART_ID + '-tooltip';
        private static REMAINING_CHART_TOOLTIP_ID: string = SimUsageController.REMAINING_CHART_ID + '-tooltip';

        public selectedSim: Models.ISIM;
        public dataUsage: IPaginatedList<SIMDataUsages>;
        public dataRemaining: Models.IDataRemaining;
        public currentCycleTotalDataUsage: number;
        public paginationSize: number;
        public paginationPage: number;

        private simService: Services.SIMService;
        private simPlanService: Services.SimPlanService;
        private modalService: ngModal.IModalService;
        private $timeout: ng.ITimeoutService;
        private $scope: ng.IScope;
        private $routeParams: ng.route.IRouteParamsService;
        private timezoneService: Services.TimezoneService;
        private messageTranslationsService: Services.MessageTranslationsService;

        private usageGraph: jquery.flot.plot;
        private remainingGraph: jquery.flot.plot;
        private remainingDataGraphOptions: jquery.flot.plotOptions;
        private usageDataGraphOptions: jquery.flot.plotOptions;

        /**
         * Constructor of the class.
         */
        constructor(simService: Services.SIMService,
                    simPlanService: Services.SimPlanService,
                    modalService: ngModal.IModalService,
                    $timeout: ng.ITimeoutService,
                    $scope: ng.IScope,
                    $routeParams: ng.route.IRouteParamsService,
                    timezoneService: Services.TimezoneService,
                    messageTranslationsService: Services.MessageTranslationsService,
                    ) {
            this.simService = simService;
            this.simPlanService = simPlanService;
            this.modalService = modalService;
            this.$timeout = $timeout;
            this.$scope = $scope;
            this.$routeParams = $routeParams;
            this.timezoneService = timezoneService;
            this.messageTranslationsService = messageTranslationsService;

            $timeout(() => {
                window.altair_admin_common_init();
            }, 500);

            this.initData();

            this.$scope.$on('$destroy', () => {
                this.unbindEvents();
                if (this.usageGraph !== undefined) {
                    this.usageGraph.destroy();
                    this.usageGraph = undefined;
                }
                if (this.remainingGraph !== undefined) {
                    this.remainingGraph.destroy();
                    this.remainingGraph = undefined;
                }
            });
        }

        public getCurrentCycleString(): string {
            if (!this.selectedSim)
                return "";
            let init: Date = this.getCurrentCycleInit();
            let end: Date = this.getCurrentCycleEnd();
            return init.getDate() + "/" + (init.getMonth() + 1) + "/" + init.getFullYear() +
                " to " + end.getDate() + "/" + (end.getMonth() + 1) + "/" + end.getFullYear();
        }

        public getCurrentCycleInit(): Date {
            if (!this.selectedSim) return undefined;
            let now: Date = new Date();
            let currentMonth: number = now.getMonth();
            let currentYear: number = now.getFullYear();
            let init: Date = new Date(currentYear, currentMonth, this.selectedSim.billingCycle);
            if (init > now) init.setMonth(init.getMonth() - 1);
            return init;
        }

        public getCurrentCycleEnd(): Date {
            if (!this.selectedSim) return undefined;
            let now: Date = new Date();
            let currentMonth: number = now.getMonth();
            let currentYear: number = now.getFullYear();
            let init: Date = new Date(currentYear, currentMonth, this.selectedSim.billingCycle);
            if (init > now) init.setMonth(init.getMonth() - 1);
            let end: Date = new Date(init.getFullYear(), init.getMonth() + 1, this.selectedSim.billingCycle);
            end.setDate(end.getDate() - 1); end.setHours(23); end.setMinutes(59); end.setSeconds(59); end.setMilliseconds(999);
            if (init.getDate() === 31 && init.getMonth() === 0) // if the cycle starts on Jan 31 it must end on the last day of Feb
                end = new Date(init.getFullYear(), 2, 0);
            return end;
        }

        public getRemainingData(refresh?: boolean): void {
            if (refresh || !this.dataRemaining) {
                let selectedSim: number = parseInt((this.$routeParams as IPathParameters).selectedSim, 10);
                this.simService
                    .getRemainingData(selectedSim)
                    .then((dataRemaining: number) => {
                        dataRemaining = dataRemaining > 0 ? dataRemaining : 0;
                        this.dataRemaining = Common.Utils.humanReadableBytesAsObject(dataRemaining, 0);
                        this.$timeout(() => {
                            window.altair_dashboard_init();
                        });
                        this.dataRemaining.planPercentage = dataRemaining * 100 / this.selectedSim.simPlan.dataAmount;
                        this.dataRemaining.originalValue = dataRemaining;
                    });
            }
        }

        public launchSIMModal(title: string, sim: Models.ISIM): void {
            this.simPlanService
                .getAllSimPlansPromise()
                .then((simPlans: Models.SimPlan[]) => {
                    let self: SimUsageController = this;
                    let copy: Models.SIM = sim === undefined ? Models.SIM.Empty() : Models.SIM.Copy(sim);
                    this.modalService.showModal({
                        templateUrl: Application.Controllers.SIMModalController.templateURL,
                        controller: Application.Controllers.SIMModalController.$name,
                        inputs: {
                            title: title,
                            sim: copy,
                            simPlans: simPlans,
                            newSim: false,
                        },
                    }).then((modal: ngModal.IModalObject) => {
                        let modalElem: UIkit.ModalElement = UIkit.modal(Application.Controllers.SIMModalController.ID,
                                                            {bgclose: false, modal: false, keyboard: false});
                        modalElem.show();
                        modal.scope.loadSelectize();
                        modal.close.then((result: IModalResult) => {
                            modalElem.hide();
                            if (result) {
                                self.saveSIM(result, sim);
                            }
                        });
                    });
                });
        }

        public saveSIM(result: IModalResult, sim: Models.ISIM): void {
            this.saveSIMCard(result.sim, sim, (savedSim: Models.ISIM) => {
                if (result.newRemainingData)
                    this.setDataRemaining(savedSim, result.newRemainingData);
            });
        }

        public saveSIMCard(sim: Models.ISIM, copy: Models.ISIM, callback: (sim: Models.ISIM) => void): void {
            if (sim) {
                this.simService.existsSIMByIccid(sim.iccid)
                    .then((exists: boolean) => {
                        if (exists && copy && copy.iccid !== sim.iccid)
                            // if iccid already exists and we're not updating an element
                            this.messageTranslationsService.alertOnError("Error - Iccid already exists");
                        else
                            this.simService.saveSIM(sim)
                                .then((savedSim: Models.ISIM) => {
                                    callback(savedSim);
                                    this.loadSim(true);
                                })
                                .catch(() => {
                                    this.messageTranslationsService.alertOnError("Something was wrong creating/updating the SIM");
                                });
                    });
            }
        }

        public setDataRemaining(sim: Models.ISIM, remainingData: number): void {
            let date: Date = new Date();
            this.simService.setRemainingData(sim.href, remainingData, date);
        }

        public checkBeforeSaveSIM(sim: Models.ISIM, oldSIM: Models.ISIM): void {
            if (sim !== undefined) {
                this.simService.getSIMByIccid(sim.iccid, (result: Models.SIM) => {
                    // there is a sim with that iccid, checks the requested one in case we are updating
                    if (oldSIM === undefined || oldSIM.iccid !== result.iccid) {
                        this.messageTranslationsService.alertOnError("Error - Iccid already exists");
                    } else {
                        this.simService.saveSIM(sim).then((returnedSim: Models.ISIM) => {
                            this.selectedSim = returnedSim;
                        });
                    }
                }, (reason: ngCustom.IReason) => {
                    if (reason.status === Common.NOT_FOUND) {
                        this.simService.saveSIM(sim).then((returnedSim: Models.ISIM) => {
                            this.loadSim();
                        });
                    }
                });
            }
        }

        public launchDataModal(title: string, element: Models.ISIM): void {
            this.simService
                .getRemainingData(Common.Utils.idFromHref(element.href))
                .then((remaining: number) => {
                    this.modalService
                        .showModal({
                            templateUrl: Application.Controllers.SIMDataModalController.templateURL,
                            controller: Application.Controllers.SIMDataModalController.$name,
                            inputs: {
                                title: title,
                                sim: element,
                                currentRemaining: remaining,
                            },
                        })
                        .then((modal: ngModal.IModalObject) => {
                            let modalElem: UIkit.ModalElement = UIkit.modal(Application.Controllers.SIMDataModalController.ID,
                                                                {bgclose: false, modal: false, keyboard: false});
                            modalElem.show();
                            modal.scope.loadSelectize();
                            modal.close.then((result: Object[]) => {
                                modalElem.hide();
                                if (result) {
                                    let dataAmount: number = result[0] as number;
                                    let operation: string = result[1] as string;
                                    let date: Date = result[2] as Date;
                                    if (operation === 'SET') {
                                        this.simService
                                            .setRemainingData(element.href, dataAmount, date)
                                            .then((newValue: number) => {
                                                this.loadSim(true);
                                            });
                                    } else if (operation === 'ADD') {
                                        this.simService
                                            .addData(element.href, dataAmount, date)
                                            .then((newValue: number) => {
                                                this.loadSim(true);
                                            });
                                    }
                                }
                            });
                        });
                });
        }

        public isOutOfCurrentCycle(dataUsage: Models.ISIMDataUsages): boolean {
            let cycleInit: Date = this.getCurrentCycleInit();
            return (new Date(dataUsage.date as string) <= cycleInit) ? true : false;
        }

        public changePageNumber(page: number): void {
            if (this.selectedSim !== undefined) {
                this.paginationPage = page;
                this.simService
                    .getDataUsage(Common.Utils.idFromHref(this.selectedSim.href), this.paginationPage, this.paginationSize)
                    .then((dataUsage: IPaginatedList<SIMDataUsages>) => {
                        this.dataUsage = dataUsage;
                    });
            }
        }

        private initData(): void {
            this.paginationPage = 0;
            this.paginationSize = 9;
            this.loadSim();
        }

        private loadSim(refreshRemainingData?: boolean): void {
            let selectedSim: number = parseInt((this.$routeParams as IPathParameters).selectedSim, 10);
            if (selectedSim > 0) this.selectedSim = new Models.SIM();
            this.simService
                .getSIMAndPlanBySIMIdPromise(selectedSim)
                .then((sim: Models.SIM) => {
                    this.selectedSim = sim;
                    this.simService
                        .getDataUsage(selectedSim, this.paginationPage, this.paginationSize)
                        .then((dataUsage: IPaginatedList<SIMDataUsages>) => {
                            this.dataUsage = dataUsage;
                            this.getRemainingData(refreshRemainingData);
                            this.simService
                                .getDataUsageChart(selectedSim, undefined, undefined, 30)
                                .then((dataUsageChart: Models.ISIMDataUsageChart) => {
                                    this.drawUsageDataChart(dataUsageChart);
                                });
                            this.simService
                                .getRemainingDataChart(selectedSim, undefined, undefined, 300)
                                .then((remainingDataChart: Models.ISIMDataRemainingChart) => {
                                    this.drawRemainingDataChart(remainingDataChart);
                                });
                            this.simService
                                .getTotalDataUsage(selectedSim, this.getCurrentCycleInit())
                                .then((totalDataUsage: number) => {
                                    this.currentCycleTotalDataUsage = totalDataUsage;
                                });
                        });
                })
                .catch((reason: ngCustom.IReason) => {
                    this.selectedSim = undefined;
                });
        }

        private drawUsageDataChart(dUsageDigested: Models.ISIMDataUsageChart): void {
            // preparing array of points [ [x1, y1], [x2, y2], ...]
            let points: number[][] = new Array();
            dUsageDigested.values.forEach((point: Models.ISIMDataUsageChartPoint) => {
                points.push([point.timestamp , point.value]);
            });
            // // tunning up the graph
            this.setUsageDataGraphOptions(points);
            // drawing the graph
            this.usageGraph = $.plot($(SimUsageController.USAGE_CHART_ID), [{data: points}], this.usageDataGraphOptions);
            // // binding the hover event to show the tooltip
            let chart: JQuery.IJQueryFlotBind =  $(SimUsageController.USAGE_CHART_ID);
            chart.bind("plothover", (event: JQueryEventObject, pos: jquery.flot.pos, item: jquery.flot.item) => {
                if (item) {
                    let x: Date = new Date(item.datapoint[0]);
                    let xString: string = x.getDate() + '/' + (x.getMonth() + 1) + '/' + x.getFullYear();
                    let y: string = Common.Utils.humanReadableBytes(item.datapoint[1]);
                    $(SimUsageController.USAGE_CHART_TOOLTIP_ID)
                        .html(xString + '<br/>' + y)
                        .css({top: item.pageY - 35, left: item.pageX - 60})
                        .fadeIn(200);
                } else {
                    $(SimUsageController.USAGE_CHART_TOOLTIP_ID).hide();
                }
            });
        }

        private drawRemainingDataChart(dataRemaining: Models.ISIMDataUsageChart): void {
            // preparing array of points [ [x1, y1], [x2, y2], ...]
            let points: number[][] = new Array();
            dataRemaining.values.forEach((point: Models.ISIMDataRemainingChartPoint) => {
                points.push([dataRemaining.init + point.x * dataRemaining.stepSize , point.value]);
            });
            // tunning up the graph
            this.setRemainingDataGraphOptions(dataRemaining);
            // drawing the graph
            this.remainingGraph = $.plot($(SimUsageController.REMAINING_CHART_ID), [
                {
                    data: points,
                    threshold: {
                        below: 0,
                        color: "#c00000",
                    },
                }], this.remainingDataGraphOptions);
            // binding the hover event to show the tooltip
            let chart: JQuery.IJQueryFlotBind =  $(SimUsageController.REMAINING_CHART_ID);
            chart.bind("plothover", (event: JQueryEventObject, pos: jquery.flot.pos, item: jquery.flot.item) => {
                if (item) {
                    let x: Date = new Date(item.datapoint[0]);
                    let xString: string = x.getDate() + '/' + (x.getMonth() + 1) + '/' + x.getFullYear();
                    let y: string = Common.Utils.humanReadableBytes(item.datapoint[1]);
                    $(SimUsageController.REMAINING_CHART_TOOLTIP_ID)
                        .html(xString + '<br/>' + y)
                        .css({top: item.pageY - 35, left: item.pageX - 60})
                        .fadeIn(200);
                } else {
                    $(SimUsageController.REMAINING_CHART_TOOLTIP_ID).hide();
                }
            });
        }

        // private setUsageDataGraphOptions(dataUsage: Models.ISIMDataUsageChart): void {
        private setUsageDataGraphOptions(dataUsage: number[][]): void {
            let max: number = 0;
            dataUsage.forEach((du: number[]) => {
                if (du[1] > max) max = du[1];
            });
            this.usageDataGraphOptions = {
                colors: ['#93DFF5'],
                series: {
                    stack: true,
                    bars: {
                        show: true,
                        barWidth: 24 * 60 * 60 * 1000,
                        fill: true,
                        align: 'right',
                    },
                },
                grid: {
                    hoverable: true,
                    backgroundColor: '#626360',
                    markings: [{
                        xaxis: {
                            from: this.getCurrentCycleInit(),
                            to: this.getCurrentCycleEnd(),
                        }, color: '#434442',
                    }],
                 },
                yaxes: [
                    {
                        axisLabelColour: "lightslategray",
                        min: 0,
                        tickFormatter: function (value: number, axis: jquery.flot.axis): string {
                            return Common.Utils.humanReadableBytes(value, 1);
                        },
                        max: max,
                    },
                ],
                xaxis: {
                    mode: "time",
                    tickSize: [4, "day"],
                    minTickSize: [1, "day"],
                    // tickFormatter: function (value: number, axis: jquery.flot.axis): string {
                    //         return "" + new Date(value).getDate();
                    // },
                    // min x value equal to the begining of the billing cycle
                    // min: this.search/findAllBySimIdOrderByDateDesc?sim_id=(),
                    // min x value equal to the first value available
                    // min: this.dataUsageChartData.init + this.dataUsageChartData.values[0].x * this.dataUsageChartData.stepSize,
                    // max x value equal to the last value available
                    // max: dataUsage.init + dataUsage.values[dataUsage.values.length - 1].x * dataUsage.stepSize, // !!!!!!!!!!!!!
                    timezone: "browser",
                    axisLabelUseCanvas: true,
                    tickColor: "gray",
                },
                yaxis: {
                    axisLabelUseCanvas: true,
                    tickColor: "gray",
                    // min: dataUsage.minValue,
                    max: max,
                },
                legend: false,
            };
        }

        private setRemainingDataGraphOptions(dataRemaining: Models.ISIMDataUsageChart): void {
            this.remainingDataGraphOptions = {
                colors: ['#93DFF5'],
                series: {
                    lines: { show: true, lineWidth: 2.0, fill: true },
                },
                grid: {
                    hoverable: true,
                    backgroundColor: "#434442",
                },
                yaxis: {
                    axisLabelColour: "lightslategray",
                    tickFormatter: function (value: number, axis: jquery.flot.axis): string {
                        if (typeof(value) !== 'number')
                            return '0 B';
                        else if (value === 0)
                            return '0 B';
                        else if (value > 0)
                            return Common.Utils.humanReadableBytes(value, 1);
                        else
                            return '-' + Common.Utils.humanReadableBytes(-1 * value, 0);
                    },
                    axisLabelUseCanvas: true,
                    tickColor: "gray",
                    // min: dataRemaining.minValue,
                    // max: 1000000000,
                },
                xaxis: {
                    mode: "time",
                    minTickSize: [1, "hour"],
                    // min x value equal to the begining of the billing cycle
                    // min: this.getCurrentCycleInit(),
                    // min x value equal to the first value available
                    // min: this.dataUsageChartData.init + this.dataUsageChartData.values[0].x * this.dataUsageChartData.stepSize,
                    // max x value equal to the last value available
                    // max: dataRemaining.init + dataRemaining.values[dataRemaining.values.length - 1].x * dataRemaining.stepSize,
                    timezone: "browser",
                    axisLabelUseCanvas: true,
                    tickColor: "gray",
                },
                legend: false,
            };
        }

        private unbindEvents(): void {
            if ($(SimUsageController.REMAINING_CHART_ID).length) {
                $(SimUsageController.REMAINING_CHART_ID).unbind("plothover");
            }

            if ($(SimUsageController.USAGE_CHART_ID).length) {
                $(SimUsageController.USAGE_CHART_ID).unbind("plothover");
            }
        }
    }
}